"use client"

import React, {useEffect, useState} from 'react';
import {loadAlertSetting, TAlertSettingPublic} from "@/service/IndexService";
import {parse, isValid} from "date-fns";

type NoticeType = 'info' | 'warning';

const parseDateTime = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    try {
        const parsed = parse(dateStr, 'yyyy-MM-dd HH:mm', new Date());
        return isValid(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

export default function SystemNotice() {
    const [setting, setSetting] = useState<TAlertSettingPublic | null>(null);
    const [showNotice, setShowNotice] = useState(false);

    useEffect(() => {
        loadAlertSetting().then((data) => {
            if (data) {
                setSetting(data);

                const currentTime = new Date();
                const startTime = parseDateTime(data.startTime);
                const endTime = parseDateTime(data.endTime);

                const isWithinTime =
                    (!startTime || currentTime >= startTime) &&
                    (!endTime || currentTime <= endTime);

                setShowNotice(data.enabled && isWithinTime && data.content.trim() !== '');
            }
        });
    }, []);

    if (!showNotice || !setting) {
        return null;
    }

    const noticeType: NoticeType = setting.level === 'WARNING' ? 'warning' : 'info';

    const styles = {
        info: {
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            text: 'text-blue-800',
            padding: 'py-3',
            fontSize: 'text-sm',
            fontWeight: 'font-medium',
            icon: (
                <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
            )
        },
        warning: {
            bg: 'bg-red-100',
            border: 'border-red-400 border-b-2',
            text: 'text-red-900',
            padding: 'py-5',
            fontSize: 'text-base',
            fontWeight: 'font-bold',
            icon: (
                <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
            )
        }
    };

    const currentStyle = styles[noticeType];

    return (
        <div className={`w-full ${currentStyle.bg} border-b ${currentStyle.border}`}>
            <div className={`max-w-[1280px] mx-auto px-4 lg:px-8 ${currentStyle.padding}`}>
                <div className={`flex items-center justify-center ${currentStyle.fontSize} ${currentStyle.text}`}>
                    {currentStyle.icon}
                    <span className={currentStyle.fontWeight}>
                        {setting.content}
                    </span>
                </div>
            </div>
        </div>
    );
}
